import { useRef } from "react";
import { motion, useInView, animate, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

const METRICS = [
  { key: "years", value: 300, suffix: "+", label: "Man-years of cybersecurity experience" },
  { key: "smes", value: 50, suffix: "+", label: "Dedicated cybersecurity SMEs" },
  { key: "support", value: 24, suffix: "×7", label: "Round-the-clock monitoring & response" },
  { key: "advisors", value: 100, suffix: "%", label: "Certified trusted advisors" },
];

function Counter({ to, suffix, inView, decimals = 0 }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(decimals ? v.toFixed(decimals) : Math.round(v).toString()),
    });
    return () => controls.stop();
  }, [inView, to, decimals, mv]);

  return (
    <>
      <span>{display}</span>
      <span>{suffix}</span>
    </>
  );
}

export default function WhyMetrics() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      data-testid="why-metrics"
      className="theme-editorial relative py-16 md:py-24"
      aria-label="Why RockLadder"
    >
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.32em] text-[#8A8A8A] mb-4">
            [ WHY · ROCKLADDER ]
          </div>
          <h2 className="font-editorial-display text-[42px] md:text-[68px] leading-[0.98] tracking-[-0.028em] text-[#0B0B10]">
            The numbers behind the{" "}
            <span className="font-editorial-italic text-[#5B2A86]" style={{ fontStyle: "italic", fontWeight: 400 }}>confidence.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-[rgba(17,17,17,0.12)]">
          {METRICS.map((m, i) => (
            <div
              key={m.key}
              data-testid={`metric-${m.key}`}
              className="py-10 md:py-14 border-b border-r-0 lg:border-r last:border-r-0 border-[rgba(17,17,17,0.12)] pr-6"
            >
              <div className="font-editorial-display text-[64px] md:text-[96px] leading-[0.92] tracking-[-0.03em] text-[#0B0B10]">
                <Counter to={m.value} suffix={m.suffix} inView={inView} />
              </div>
              <div className="mt-6 font-mono text-[10px] tracking-[0.28em] text-[#5B2A86] mb-3">
                / 0{i + 1}
              </div>
              <p className="text-[14.5px] leading-[1.5] text-[#4A4A55] max-w-[240px]">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
